import {
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Button,
  Select,
  Tag,
} from "antd";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  addInvoice,
  getallProjectForReceipts,
  getCustomersForReceipts,
  getLastInvoiceNumber,
} from "../../utils/API";
import { AuthContext } from "../../Auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { downloadInvoicePdf } from "../../utils/invoicePdf";
import debounce from "lodash/debounce";
import {
  markInvoiceAutoDraftPending,
  markInvoiceAutoDraftCompleted,
  clearInvoiceAutoDraftPending,
  resolveInvoiceNumberForCreate,
  getNextSavedInvoicePreviewSequence,
} from "../../utils/invoiceNumber";
interface CurrencyState {
  selectedCurrency: string;
  rate: number | null;
}
const currencies = [
  { symbol: "$",code: "USD", name: "US Dollar",  rate: null },
  { symbol: "€",code: "EUR", name: "Euro", rate: null },
];
const CUSTOM_VALUE_PREFIX = "custom_";
const isValidObjectId = (id: string): boolean => {
  return /^[0-9a-fA-F]{24}$/.test(id);
};
const hasItemContent = (item: any) => {
  if (!item || typeof item !== "object") {
    return false;
  }
  return [
    item.description,
    item.hsn,
    item.gst,
    item.originalAmount,
    item.amount,
  ].some((value) => {
    if (value === 0 || value === "0") {
      return true;
    }
    return String(value ?? "").trim() !== "";
  });
};
const hasMeaningfulInvoiceData = (values: any) => {
  if (!values || typeof values !== "object") {
    return false;
  }
  const hasMainDetails = [
    values.customer_name,
    values.address,
    values.place_of_supply,
    values.project,
    values.invoice_date,
    values.due_date,
    values.payment_link,
    values.email,
  ].some((value) => String(value ?? "").trim() !== "");
  const hasItems = (values.items || []).some((item: any) => hasItemContent(item));
  return hasMainDetails || hasItems;
};
const AddInvoicePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [submitMode, setSubmitMode] = useState<"save" | "draft" | null>(null);
  const [form] = Form.useForm();
  const latestValuesRef = useRef<any>({});
  const skipAutoDraftRef = useRef(false);
  const isAutoDraftingRef = useRef(false);
  const invoiceSeqRef = useRef(1);
  const selectedCurrencyRef = useRef<string | undefined>(undefined);
  const exchangeRateRef = useRef<number | null>(null);
  const items = Form.useWatch("items", form);
  const cgstPercent = Form.useWatch("cgst_percent", form);
  const sgstPercent = Form.useWatch("sgst_percent", form);
  const igstPercent = Form.useWatch("igst_percent", form);
  const selectedCurrency = Form.useWatch("currency", form);
  const invoiceDate = Form.useWatch("invoice_date", form);
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [customers, setCustomers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [invoiceSeq, setInvoiceSeq] = useState(1);
  const [customerSelected, setCustomerSelected] = useState(false);
  const [customCustomerName, setCustomCustomerName] = useState("");
  const [currencyState, setCurrencyState] = useState<CurrencyState>({
    selectedCurrency: "INR",
    rate: null,
  });
  const { authData } = useContext(AuthContext);
  const navigate = useNavigate();
  const prefix = "INV";
  const year = dayjs().format("YYYY");
  const formatSequence = (num: number) => num.toString().padStart(4, "0");
  const generateInvoiceNumber = (seq: number) =>
    `${prefix}-${year}-${formatSequence(seq)}`;
  const buildInvoiceData = async (
    values: any,
    invoiceStatus: "sent" | "drafted",
    mode: "save" | "draft"
  ) => {
    const invoiceNumber =
      invoiceStatus === "drafted"
        ? (await resolveInvoiceNumberForCreate("drafted")).invoiceNumber
        : (await resolveInvoiceNumberForCreate("sent")).invoiceNumber;
    let customerId = null;
    let customCustomerNameResolved = null;
    if (customerSelected && customCustomerName) {
      // User selected from master then edited the name — use edited name
      const originalCustomer = customers.find((c: any) =>
        c._id === values.customer_name || c.name === customCustomerName
      );
      customerId = originalCustomer?._id || null;
      customCustomerNameResolved = customCustomerName;
    } else if (
      typeof values.customer_name === "string" &&
      values.customer_name.startsWith(CUSTOM_VALUE_PREFIX)
    ) {
      customCustomerNameResolved = values.customer_name.replace(CUSTOM_VALUE_PREFIX, "");
    } else if (
      typeof values.customer_name === "string" &&
      isValidObjectId(values.customer_name)
    ) {
      customerId = values.customer_name;
    } else if (typeof values.customer_name === "string") {
      customCustomerNameResolved = values.customer_name;
    }
    const invoiceData: any = {
      ...values,
      customer_name: customerId,
      custom_customer_name: customCustomerNameResolved,
      invoice_number: invoiceNumber,
      invoice_status: invoiceStatus,
      invoice_date: values.invoice_date
        ? dayjs(values.invoice_date).format("DD-MM-YYYY")
        : null,
      due_date: values.due_date
        ? dayjs(values.due_date).format("DD-MM-YYYY")
        : null,
      taxable_amount: Number(values.taxable_amount || 0),
      grand_total: Number(values.grand_total || 0),
      cgst_amount: Number(values.cgst_amount || 0),
      sgst_amount: Number(values.sgst_amount || 0),
      igst_amount: Number(values.igst_amount || 0),
      cgst_percent: Number(values.cgst_percent || 0),
      sgst_percent: Number(values.sgst_percent || 0),
      igst_percent: Number(values.igst_percent || 0),
      amount: Number(values.amount || 0),
      payment_link: values.payment_link || "",
      email: values.email || "",
      items: (values.items || [])
        .filter((item: any) => hasItemContent(item))
        .map((item: any) => ({
          description: item.description,
          hsn: Number(item.hsn || 0),
          gst: Number(item.gst || 0),
          amount: Number(item.amount || 0),
          originalAmount: Number(item.originalAmount || item.amount || 0),
        })),
    };
    if (
      selectedCurrencyRef.current !== "INR" &&
      selectedCurrencyRef.current &&
      exchangeRateRef.current
    ) {
      invoiceData.currency_conversion = {
        original_currency: selectedCurrencyRef.current,
        original_amount: (invoiceData.items || []).reduce(
          (sum: number, item: any) => sum + (Number(item.originalAmount) || 0),
          0
        ),
        exchange_rate: exchangeRateRef.current,
        converted_inr_amount:
          Number(values.grand_total || 0) * Number(exchangeRateRef.current || 0),
      };
      invoiceData.currency = selectedCurrencyRef.current;
    } else {
      invoiceData.currency = "INR";
    }
    return invoiceData;
  };
  const fetchCustomers = async (searchText = "") => {
    try {
      const res: any = await getCustomersForReceipts(searchText, "", true);
      if (res?.data) {
        setCustomers(res.data.filter((c: any) => c.isActive));
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };
  const customerOptions = useMemo(() => {
    const list = customers.map((customer: any) => ({
      label: customer.name,
      value: customer._id,
    }));
    const typed = customerSearchText.trim();
    const hasExactMatch = customers.some(
      (customer: any) =>
        (customer.name || "").toLowerCase() === typed.toLowerCase(),
    );
    if (typed && !hasExactMatch) {
      list.unshift({
        label: `${typed}`,
        value: `${CUSTOM_VALUE_PREFIX}${typed}`,
      });
    }
    return list;
  }, [customers, customerSearchText]);
  const debouncedSearchCustomers = useMemo(
    () =>
      debounce((searchText: string) => {
        fetchCustomers(searchText);
      }, 500),
    []
  );
  useEffect(() => {
    return () => {
      debouncedSearchCustomers.cancel();
    };
  }, [debouncedSearchCustomers]);
  const handleCurrencyChange = (currencyCode: string) => {
    const currentItems = form.getFieldValue("items") || [];
    if (currencyCode === "INR") {
      const updatedItems = currentItems.map((item: any) => {
        const visibleAmount = Number(item.originalAmount || item.amount || 0);
        return {
          ...item,
          originalAmount: visibleAmount,
          amount: visibleAmount,
        };
      });
      form.setFieldsValue({ items: updatedItems });
      setCurrencyState({
        selectedCurrency: "INR",
        rate: null,
      });
      form.setFieldsValue({
        currency: "INR",
        exchange_rate: undefined,
      });
    } else {
      const currency = currencies.find((c) => c.code === currencyCode);
      const newRate = currency?.rate ?? null;
      const updatedItems = currentItems.map((item: any) => {
        const visibleAmount = Number(item.originalAmount || item.amount || 0);
        const convertedInrAmount = newRate && newRate > 0
          ? visibleAmount * newRate
          : visibleAmount;
        return {
          ...item,
          originalAmount: visibleAmount,
          amount: convertedInrAmount,
        };
      });
      form.setFieldsValue({ items: updatedItems });
      setCurrencyState({
        selectedCurrency: currencyCode,
        rate: newRate,
      });
      form.setFieldsValue({ currency: currencyCode });
    }
  };
  const handleCurrencyRateChange = (value: string) => {
    let newRate = parseFloat(value);
    if (isNaN(newRate)) newRate = 0;
    setCurrencyState((prev) => ({
      ...prev,
      rate: newRate,
    }));
    const currentItems = form.getFieldValue("items") || [];
    const updatedItems = currentItems.map((item: any) => {
      const originalAmount = Number(item.originalAmount || 0);
      const convertedInrAmount = newRate > 0
        ? originalAmount * newRate
        : originalAmount;
      return {
        ...item,
        originalAmount,
        amount: convertedInrAmount,
      };
    });
    form.setFieldsValue({ items: updatedItems });
  };
  useEffect(() => {
    if (selectedCurrency && selectedCurrency !== "INR") {
      form.setFieldsValue({
        cgst_percent: 0,
        sgst_percent: 0,
        igst_percent: 0,
        cgst_amount: 0,
        sgst_amount: 0,
        igst_amount: 0,
      });
    } else if (selectedCurrency === "INR") {
      form.setFieldsValue({
        cgst_percent: 9.0,
        sgst_percent: 9.0,
        igst_percent: 18,
      });
    }
  }, [selectedCurrency, form]);
  useEffect(() => {
    invoiceSeqRef.current = invoiceSeq;
  }, [invoiceSeq]);
  useEffect(() => {
    selectedCurrencyRef.current = selectedCurrency;
  }, [selectedCurrency]);
  useEffect(() => {
    exchangeRateRef.current = currencyState.rate;
  }, [currencyState.rate]);
  useEffect(() => {
    latestValuesRef.current = form.getFieldsValue(true);
  }, [form]);
  useEffect(() => {
    return () => {
      // DISABLED: Auto-draft creation on component unmount
      // Drafts should only be created when user explicitly clicks "Save as Draft"
      // Back navigation should not create or store any draft invoice automatically
      return;
    };
  }, [form]);
  const submitInvoice = async (
    values: any,
    invoiceStatus: "sent" | "drafted",
    mode: "save" | "draft"
  ) => {
    setIsLoading(true);
    setSubmitMode(mode);
    skipAutoDraftRef.current = true;
    try {
      const invoiceData = await buildInvoiceData(values, invoiceStatus, mode);
      console.log("Sending invoice data:", invoiceData);
      const response = await addInvoice(invoiceData);
      if (response) {
        const isSuccess =
          response.success === true ||
          response.status === "success" ||
          !!response.data ||
          !!response._id ||
          !!response.id ||
          (response.message &&
            response.message.toLowerCase().includes("success"));
        if (isSuccess) {
          message.success(
            invoiceStatus === "drafted"
              ? "Invoice draft saved successfully!"
              : "Invoice added successfully!"
          );
          form.resetFields();
          if (invoiceStatus === "drafted") {
            navigate("/invoice");
            return;
          }
          if (response.data && response.data._id) {
            navigate(`/invoice/edit/${response.data._id}`);
          } else if (response._id) {
            navigate(`/invoice/edit/${response._id}`);
          } else {
            message.info("Invoice created successfully, but redirecting to list");
            navigate("/invoice");
          }
        } else {
          const errorMessage = response.message || response.error || "Failed to save invoice";
          throw new Error(errorMessage);
        }
      } else {
        throw new Error("No response received from server");
      }
    } catch (error: any) {
      console.error("Error saving invoice:", error);
      skipAutoDraftRef.current = false;
      let errorMessage = "Error Saving Invoice";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
      setSubmitMode(null);
    }
  };
  const onFinish = async (values: any) => {
    await submitInvoice(values, "sent", "save");
  };
  const handleSaveDraft = async () => {
    const values = form.getFieldsValue(true);
    await submitInvoice(values, "drafted", "draft");
  };
  const handlePreview = async () => {
    try {
      const values = await form.validateFields();
      
      let customerName = values.customer_name;
      if (customerSelected && customCustomerName) {
        // Use the edited name from the text input
        customerName = customCustomerName;
      } else if (
        typeof customerName === "string" &&
        customerName.startsWith(CUSTOM_VALUE_PREFIX)
      ) {
        customerName = customerName.replace(CUSTOM_VALUE_PREFIX, "");
      } else {
        const selectedCustomer = customers.find(
          (c) => c._id === values.customer_name
        );
        customerName = selectedCustomer?.name || values.customer_name || "-";
      }
      const previewData = {
        ...values,
        customer_name: customerName,
        invoice_status: values.invoice_status || "sent",
        invoice_number: generateInvoiceNumber(invoiceSeq),
        invoice_date: values.invoice_date
          ? dayjs(values.invoice_date).format("DD-MM-YYYY")
          : null,
      };
      downloadInvoicePdf(previewData, "preview");
    } catch (error) {
      message.error("Please fill required fields");
    }
  };
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res: any = await getallProjectForReceipts("", "", true);
        if (res?.data) {
          setProjects(res.data.filter((p: any) => p.isActive));
        }
      } catch (error) {
        console.error("Failed to fetch projects:", error);
      }
    };
    const fetchInvoiceNo = async () => {
      try {
        const nextSeq = await getNextSavedInvoicePreviewSequence();
        setInvoiceSeq(nextSeq);
      } catch (error) {
        console.error("Failed to fetch invoice number:", error);
        setInvoiceSeq(1);
      }
    };
    fetchCustomers("");
    fetchProjects();
    fetchInvoiceNo();
  }, []);
  const disablePastDates = () => false;

  const disableDueDates = (current: any) => {
    if (!current || !invoiceDate) return false;
    return current.startOf("day").isBefore(dayjs(invoiceDate).startOf("day"));
  };

  useEffect(() => {
    if (!invoiceDate) return;
    const nextDueDate = dayjs(invoiceDate).add(3, "day").startOf("day");
    const currentDueDate = form.getFieldValue("due_date");

    if (!currentDueDate || !dayjs(currentDueDate).isSame(nextDueDate, "day")) {
      form.setFieldsValue({ due_date: nextDueDate });
    }
  }, [invoiceDate, form]);

  const round2 = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;
  useEffect(() => {
    const safeItems = Array.isArray(items) ? items : [];
    const amountKey =
      selectedCurrency && selectedCurrency !== "INR" ? "originalAmount" : "amount";
    const total = round2(
      safeItems.reduce(
        (sum, item) => sum + Number(item?.[amountKey] || item?.amount || 0),
        0
      )
    );
    const cgst = Number(cgstPercent || 0);
    const sgst = Number(sgstPercent || 0);
    const igst = Number(igstPercent || 0);
    let cgstAmount = 0;
    let sgstAmount = 0;
    let igstAmount = 0;
    if (cgst > 0 || sgst > 0) {
      cgstAmount = (total * cgst) / 100;
      sgstAmount = (total * sgst) / 100;
      igstAmount = 0;
    } else if (igst > 0) {
      igstAmount = (total * igst) / 100;
    }
    const calculatedGrand = total + cgstAmount + sgstAmount + igstAmount;
    const roundedGrand = Math.round(calculatedGrand);
    const roundOff = round2(roundedGrand - calculatedGrand);
    const roundOffDisplay =
      roundOff > 0 ? `+${roundOff.toFixed(2)}` : roundOff.toFixed(2);
    form.setFieldsValue({
      taxable_amount: total,
      cgst_amount: cgstAmount,
      sgst_amount: sgstAmount,
      igst_amount: igstAmount,
      round_off: roundOffDisplay,
      grand_total: roundedGrand,
    });
  }, [items, cgstPercent, sgstPercent, igstPercent, selectedCurrency, form]);
  const sectionStyle = {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    padding: "24px",
    width: "100%",
  };
  return (
    <div style={{ padding: 24, width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 30 }}>Tax Invoice</h1>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/invoice")}
          disabled={isLoading}
          style={{ height: "40px" }}
        >
          Back
        </Button>
      </div>
      <Form
        onFinish={onFinish}
        form={form}
        layout="vertical"
        colon={false}
        style={{ margin: 0 }}
        onValuesChange={(_, allValues) => {
          latestValuesRef.current = allValues;
        }}
        initialValues={{
          items: [{}],
          invoice_date: dayjs(),
          due_date: dayjs().add(3, "day"),
          gst_no: "24AAJFE9323M1Z4",
          bank_name: "HDFC BANK LTD.",
          bank_account_number: "50200073192460",
          ifsc_code: "HDFC0000101",
          cgst_percent: 9.0,
          sgst_percent: 9.0,
          igst_percent: 18,
          taxable_amount: 0,
          cgst_amount: 0,
          sgst_amount: 0,
          igst_amount: 0,
          currency: "INR",
          invoice_status: "sent",
          payment_link: "",
          email: "",
        }}
      >
        <Row>
          <Col xs={24} sm={24}>
            <div style={sectionStyle}>
              <Form.Item name="invoice_status" hidden>
                <Input />
              </Form.Item><Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <span
                        style={{
                          fontFamily: "inter, sans-serif",
                          fontSize: "14px",
                          paddingLeft: "15px",
                          fontWeight: 600,
                        }}
                      >
                        M/S.: <span className="text-red-500">*</span>
                      </span>
                    }
                    name="customer_name"
                     required={false}
                                            rules={[{ required: true, message: "Please select or enter customer name" }]}
                    // rules={[{ required: true, message: "Please select or enter customer name" }]}
                  >
                    {customerSelected ? (
                      <Input
                        size="large"
                        className="Allinput"
                        value={customCustomerName}
                        onChange={(e) => setCustomCustomerName(e.target.value)}
                        suffix={
                          <span
                            style={{ cursor: "pointer", color: "#6B7280", fontSize: 12 }}
                            onClick={() => {
                              setCustomerSelected(false);
                              setCustomCustomerName("");
                              form.setFieldsValue({ customer_name: undefined });
                            }}
                          >
                            ✕ Change
                          </span>
                        }
                      />
                    ) : (
                    <Select
                      size="large"
                      loading={isLoading}
                      showSearch
                      filterOption={(input, option) =>
                        String(option?.label || "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onSearch={(value) => {
                        setCustomerSearchText(value);
                        debouncedSearchCustomers(value);
                      }}
                      placeholder="Select or Enter Customer Name"
                      className="CustomSelect"
                      options={customerOptions}
                      notFoundContent={
                        isLoading ? "Searching..." : "Type to add new customer"
                      }
                      onChange={(customerValue) => {
                        // Find selected customer name
                        const selected = customers.find((c: any) => c._id === customerValue);
                        const name = selected?.name
                          || (typeof customerValue === "string" && customerValue.startsWith(CUSTOM_VALUE_PREFIX)
                            ? customerValue.replace(CUSTOM_VALUE_PREFIX, "")
                            : customerValue);
                        setCustomCustomerName(name || "");
                        setCustomerSelected(true);

                        const relatedProject = projects.find(
                          (p: any) => p.clientId?._id === customerValue
                        );
                        if (relatedProject) {
                          form.setFieldsValue({ project: relatedProject._id });
                        }
                      }}
                    />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingBottom: 20,
                      paddingTop: 10,
                      paddingRight: 7,
                      paddingLeft: 25,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 8,
                        minWidth: 120,
                        textAlign: "right",
                      }}
                    >
                      Invoice Number :
                    </span>
                    <Row gutter={6} style={{ width: "100%" }}>
                      <Col span={6}>
                        <Input value={prefix} className="Allinput" readOnly />
                      </Col>
                      <Col span={8}>
                        <Input value={year} className="Allinput" readOnly />
                      </Col>
                      <Col span={10}>
                        <Input
                          value={formatSequence(invoiceSeq)}
                          className="Allinput"
                          readOnly
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="address"
                    rules={[
                      { required: true, message: "Please enter the address" },
                    ]}
                  >
                    <Input.TextArea
                      placeholder="Enter Customer Address..."
                      className="Allinputtextarea"
                    />
                  </Form.Item>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      paddingBottom: 20,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 110,
                      }}
                    >
                      Place of Supply:
                    </span>
                    <Form.Item
                      name="place_of_supply"
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input placeholder="Place of supply" className="Allinput" />
                    </Form.Item>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 110,
                      }}
                    >
                      GSTIN NO. :
                    </span>
                    <Form.Item
                      name="gstno"
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input
                        placeholder="Enter your GST NO..."
                        className="Allinput"
                      />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          paddingTop: 20,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            marginRight: 8,
                            minWidth: 140,
                            textAlign: "right",
                          }}
                        >
                          Invoice Date:
                        </span>
                        <Form.Item
                          name="invoice_date"
                          style={{ marginBottom: 0, width: "100%" }}
                          rules={[{ required: true, message: "Required" }]}
                        >
                          <DatePicker
                            className="Allinput"
                            format="DD-MM-YYYY"
                            style={{ width: "100%" }}
                            disabledDate={disablePastDates}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col xs={24}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          paddingTop: 20,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            marginRight: 8,
                            minWidth: 140,
                            textAlign: "right",
                          }}
                        >
                          Due Date:
                        </span>
                        <Form.Item
                          name="due_date"
                          style={{ marginBottom: 0, width: "100%" }}
                          rules={[
                            { required: true, message: "Required" },
                            {
                              validator: (_: any, value: any) => {
                                if (!value) {
                                  return Promise.resolve();
                                }
                          
                                const selectedDueDate = dayjs(value).startOf("day");
                                const selectedInvoiceDate = invoiceDate
                                  ? dayjs(invoiceDate).startOf("day")
                                  : null;
                          
                                if (selectedInvoiceDate && selectedDueDate.isBefore(selectedInvoiceDate)) {
                                  return Promise.reject(new Error("Due date must be equal to or later than invoice date"));
                                }
                          
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <DatePicker
                            className="Allinput"
                            format="DD-MM-YYYY"
                            style={{ width: "100%" }}
                            disabledDate={disableDueDates}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col xs={24}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          paddingTop: 20,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            marginRight: 8,
                            minWidth: 140,
                            textAlign: "right",
                          }}
                        >
                          Currency:
                        </span>
                        <Form.Item
                          name="currency"
                          initialValue="INR"
                          style={{ marginBottom: 0, width: "100%" }}
                        >
                          <Select
                            placeholder="Select Currency"
                            className="CustomSelect"
                            showSearch
                            optionFilterProp="children"
                            onChange={handleCurrencyChange}
                          >
                            <Select.Option value="INR">₹ INR</Select.Option>
                            {currencies.map((currency) => (
                              <Select.Option
                                key={currency.code}
                                value={currency.code}
                              >
                                {currency.symbol} {currency.code}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </Col>
                    {selectedCurrency !== "INR" && selectedCurrency && (
                      <Col xs={24}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            paddingTop: 20,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              marginRight: 8,
                              minWidth: 140,
                              textAlign: "right",
                            }}
                          >
                            Currency Conversion Rate:
                          </span>
                          <div style={{ width: "100%" }}>
                            <Input
                              size="large"
                              type="number"
                              placeholder={`1 ${selectedCurrency} = ? INR`}
                              value={currencyState.rate || ""}
                              onChange={(e) =>
                                handleCurrencyRateChange(e.target.value)
                              }
                              onWheel={(e) => e.currentTarget.blur()}
                              className="Allinput"
                            />
                            {currencyState.rate && (
                              <div
                                style={{
                                  marginTop: 18,
                                  textAlign: "right",
                                  fontSize: "12px",
                                  color: "#1e40af",
                                  fontWeight: 500,
                                }}
                              >
                                Current Rate: 1 {selectedCurrency} = INR {currencyState.rate.toFixed(2)}
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        {/* Items Section */}
        <Row>
          <Col xs={24} sm={24}>
            <div style={sectionStyle}>
              <Form.List name="items">
                {(fields, { add, remove }) => (
                  <>
                    <Row
                      gutter={12}
                      style={{
                        fontWeight: 600,
                        borderBottom: "1px solid #eee",
                        paddingBottom: 8,
                        marginBottom: 12,
                      }}
                    >
                      <Col span={2}>Sr.No.</Col>
                      <Col span={10}>Description</Col>
                      <Col span={3}>HSN Code</Col>
                      <Col span={3}>GST %</Col>
                      <Col span={4}>
                        {selectedCurrency !== "INR" && selectedCurrency
                          ? `Amount (${selectedCurrency})`
                          : "Amount (₹ INR)"}
                      </Col>
                      {/* <Col span={4}>
                        {selectedCurrency !== "INR" && selectedCurrency
                          ? "Amount (₹ INR)"
                          : ""}
                      </Col> */}
                      <Col span={2}></Col>
                    </Row>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Row gutter={12} key={key} align="middle">
                        <Col span={2}>
                          <Form.Item
                            name={[name, "sr_no"]}
                            initialValue={index + 1}
                          >
                            <Input
                              value={index + 1}
                              disabled
                              className="Allinput"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            {...restField}
                            name={[name, "description"]}
                            rules={[{ required: true, message: "Required" }]}
                          >
                            <Input placeholder="Description" className="Allinput" />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item
                            {...restField}
                            name={[name, "hsn"]}
                          >
                            <Input placeholder="HSN Code" className="Allinput" />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item
                            {...restField}
                            name={[name, "gst"]}
                          >
                            <Input placeholder="GST %" className="Allinput" />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "originalAmount"]}
                          >
                            <Input
                              placeholder={
                                selectedCurrency !== "INR" && selectedCurrency
                                  ? `Amount in ${selectedCurrency}`
                                  : "Amount"
                              }
                              type="number" min={0} step="0.01"
                              className="Allinput"
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) => {
                                const originalAmount =
                                  Math.max(0, Math.round((parseFloat(e.target.value) || 0) * 100) / 100);
                                const currentRate =
                                  selectedCurrency !== "INR" &&
                                  currencyState.rate
                                    ? currencyState.rate
                                    : 1;
                                const convertedAmount =
                                  originalAmount * currentRate;
                                const currentItems =
                                  form.getFieldValue("items") || [];
                                if (currentItems[name]) {
                                  currentItems[name] = {
                                    ...currentItems[name],
                                    originalAmount: originalAmount,
                                    amount: convertedAmount,
                                  };
                                  form.setFieldsValue({ items: currentItems });
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>
                        {/* <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "amount"]}
                          >
                            <Input
                              placeholder="INR Amount"
                              type="number" min={0}
                              className="Allinput"
                              readOnly
                            />
                          </Form.Item>
                        </Col> */}
                        <Col span={2} style={{ paddingBottom: "15px" }}>
                          <Button
                            danger
                            size="large"
                            onClick={() => remove(name)}
                          >
                            X
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Row align="middle" gutter={16}>
                      <Col span={10}>
                        <Button type="dashed" onClick={() => add()} block>
                          + Add Item
                        </Button>
                      </Col>
                    </Row>
                  </>
                )}
              </Form.List>
              <Row gutter={16} align="middle">
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 110,
                      }}
                    >
                      GSTIN NO. :
                    </span>
                    <Form.Item
                      name="gst_no"
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        {/* Bank and Tax Details Section */}
        <Row>
          <Col xs={24} sm={24}>
            <div style={sectionStyle}>
              <Row gutter={16} align="middle">
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 170,
                      }}
                    >
                      Bank Name :
                    </span>
                    <Form.Item
                      name="bank_name"
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      padding: 20,
                      paddingRight: "80px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 140,
                        textAlign: "right",
                      }}
                    >
                      Taxable Amount :
                    </span>
                    <Form.Item
                      name="taxable_amount"
                      style={{ marginBottom: 0, width: 200 }}
                    >
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row gutter={16} align="middle">
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 170,
                      }}
                    >
                      Bank Account Number :
                    </span>
                    <Form.Item
                      name="bank_account_number"
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      marginBottom: 10,
                      paddingTop: 18,
                      paddingRight: "80px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 90,
                        textAlign: "right",
                      }}
                    >
                      CGST % :
                    </span>
                    <Form.Item
                      name="cgst_percent"
                      style={{ marginBottom: 0, width: 80 }}
                    >
                      <Input 
                        type="number" min={0} step="0.01" 
                        className="Allinput" 
                       // readOnly={selectedCurrency !== "INR"}
                      />
                    </Form.Item>
                    <span style={{ margin: "0 10px", fontWeight: 600 }}>
                      Amount :
                    </span>
                    <Form.Item
                      name="cgst_amount"
                      style={{ marginBottom: 0, width: 120 }}
                    >
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingTop: 10,
                      paddingRight: "80px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 90,
                        textAlign: "right",
                      }}
                    >
                      SGST % :
                    </span>
                    <Form.Item
                      name="sgst_percent"
                      style={{ marginBottom: 0, width: 80 }}
                    >
                      <Input 
                        type="number" min={0} step="0.01"  
                        className="Allinput" 
                       // readOnly={selectedCurrency !== "INR"}
                      />
                    </Form.Item>
                    <span style={{ margin: "0 10px", fontWeight: 600 }}>
                      Amount :
                    </span>
                    <Form.Item
                      name="sgst_amount"
                      style={{ marginBottom: 0, width: 120 }}
                    >
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row gutter={16} align="middle">
                <Col xs={24} md={12}>
                  <div
                    style={{ display: "flex", alignItems: "center", paddingTop: 20 }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 170,
                      }}
                    >
                      IFSC Code :
                    </span>
                    <Form.Item
                      name="ifsc_code"
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingTop: 18,
                      paddingRight: "80px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 90,
                        textAlign: "right",
                      }}
                    >
                      IGST % :
                    </span>
                    <Form.Item
                      name="igst_percent"
                      style={{ marginBottom: 0, width: 80 }}
                    >
                      <Input 
                        type="number" min={0} step="0.01" 
                        className="Allinput" 
                        //readOnly={selectedCurrency !== "INR"}
                      />
                    </Form.Item>
                    <span style={{ margin: "0 10px", fontWeight: 600 }}>
                      Amount :
                    </span>
                    <Form.Item
                      name="igst_amount"
                      style={{ marginBottom: 0, width: 120 }}
                    >
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row gutter={16} align="middle" style={{ marginTop: 20 }}>
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 170,
                      }}
                    >
                      Payment Link :
                    </span>
                    <Form.Item
                      name="payment_link"
                      style={{ marginBottom: 0, flex: 1 }}
                      rules={[
                        {
                          type: "url",
                          message: "Please enter a valid URL",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter payment link (e.g., https://pay.example.com/invoice/123)"
                        className="Allinput"
                      />
                    </Form.Item>
                    <Button
                      type="default"
                      style={{ marginLeft: 8 }}
                      onClick={() => {
                        const paymentLink = form.getFieldValue("payment_link");
                        if (!paymentLink) {
                          message.warning("No payment link to copy");
                          return;
                        }
                        const fallbackInput = document.createElement("textarea");
                        fallbackInput.value = paymentLink;
                        fallbackInput.setAttribute("readonly", "");
                        fallbackInput.style.position = "absolute";
                        fallbackInput.style.left = "-9999px";
                        document.body.appendChild(fallbackInput);
                        fallbackInput.select();
                        try {
                          const copied = document.execCommand("copy");
                          document.body.removeChild(fallbackInput);
                          if (copied) {
                            message.success("Payment link copied");
                          } else {
                            message.error("Failed to copy payment link");
                          }
                        } catch {
                          document.body.removeChild(fallbackInput);
                          message.error("Failed to copy payment link");
                        }
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </Col>
              </Row>
              <Row gutter={16} align="middle" style={{ marginTop: 20 }}>
                <Col xs={24} md={12}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 10,
                        minWidth: 170,
                      }}
                    >
                      Email : 
                    </span>
                    <Form.Item
                      name="email"
                      style={{ marginBottom: 0, flex: 1 }}
                      normalize={(value) => value?.toLowerCase()}
                      rules={[
                        {
                          type: "email",
                          message: "Please enter a valid email address",
                        },
                      ]}
                      
                    
                      
                    >
                      <Input placeholder="Enter email address" className="Allinput" />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row style={{ marginTop: 10 }} justify="end">
                <Col xs={24} md={12}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingTop: 18,
                      paddingRight: "80px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        marginRight: 12,
                      }}
                    >
                      Round Off :
                    </span>
                    <Form.Item
                      name="round_off"
                      style={{ marginBottom: 0, width: 180 }}
                    >
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row style={{ marginTop: 20 }} justify="end">
                <Col xs={24} md={12}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingRight: "80px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 16,
                        marginRight: 12,
                      }}
                    >
                      Grand Total :
                    </span>
                    <Form.Item
                      name="grand_total"
                      style={{ marginBottom: 0, width: 180 }}
                    >
                      <Input className="Allinput" readOnly />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row justify="end">
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  xl={24}
                  xxl={20}
                  className="d-flex justify-content-end me-4"
                >
                  <div className="button-wrapper" style={{ marginTop: 80 }}>
                    <button
                      onClick={handlePreview}
                      type="button"
                      disabled={isLoading}
                      style={{
                        background: "#fff",
                        border: "1px solid #d9d9d9",
                        height: "40px",
                        padding: "0 16px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "10px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#fff";
                      }}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={handleSaveDraft}
                      style={{
                        background: "#f8fafc",
                        border: "1px solid #cbd5e1",
                        color: "#334155",
                        height: "40px",
                        padding: "0 16px",
                        borderRadius: "6px",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "10px",
                        opacity: isLoading ? 0.7 : 1,
                      }}
                    >
                      {submitMode === "draft" ? "Saving Draft..." : "Save as a Draft"}
                    </button>
                    <button
                      className="allsubmitebutton w-20"
                      type="submit"
                      disabled={isLoading}
                    >
                      {submitMode === "save" ? "Saving..." : "Save"}
                    </button>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};
export default AddInvoicePage;




































































